import React, { useState, useEffect, useRef, useMemo } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useWordsData } from '../../lib/useWordsData';
import { useWordAudio } from '../../lib/useWordAudio';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './SentenceComprehension.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const IMAGE_MAP = {
  'поздравлять': ['boy_gift_girl', 'girl_gift_boy'],
  'бежать_за': ['dog_girl', 'girl_dog'],
  'сидеть_на': ['butterfly_flower', 'butterfly_buton']
};

const WORDS_BY_AGE = {
  '4': ['поздравлять', 'бежать_за', 'сидеть_на'],
  '5': ['поздравлять', 'бежать_за', 'сидеть_на'],
  '6': ['поздравлять', 'бежать_за', 'сидеть_на'],
  '7-9': ['поздравлять', 'бежать_за', 'сидеть_на'],
  '10-12': ['поздравлять', 'бежать_за', 'сидеть_на'],
  '13-15': ['поздравлять', 'бежать_за', 'сидеть_на'],
  '16+': ['поздравлять', 'бежать_за', 'сидеть_на'],
};

const INVERSION_LABELS = {
  'поздравлять': '«мальчик поздравляет девочку» / «девочка поздравляет мальчика»',
  'бежать_за': '«собака бежит за девочкой» / «девочка бежит за собакой»',
  'сидеть_на': '«бабочка сидит на цветке» / «бабочка сидит на бутоне»',
};

const SentenceComprehension = ({ ageGroup, onBack, onComplete }) => {
  const { data: wordsData } = useWordsData();
  const { play: playWordAudio, stop: stopWordAudio } = useWordAudio();
  const { play: playInstruction, stop: stopInstruction } = useInstructionAudio();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [answerLog, setAnswerLog] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [hasSpoken, setHasSpoken] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);

  const hasSpokenRef = useRef(false);

  const shuffleArray = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const questions = useMemo(() => {
    if (!wordsData) return [];
    const wordsForAge = WORDS_BY_AGE[ageGroup] || WORDS_BY_AGE['4'];
    return wordsForAge
      .filter(wordKey => wordsData.verbs[wordKey])
      .map(wordKey => {
        const wordData = wordsData.verbs[wordKey];
        const imgs = IMAGE_MAP[wordKey] || ['placeholder', 'placeholder'];
        return {
          id: wordKey,
          correctPhrase: wordData.display_singular,
          correctPhraseAudioUrl: wordData.display_singular_audio_url ?? null,
          options: {
            singular: { key: 'singular', image: `${S3_BASE_URL}/${imgs[0]}.png` },
            plural: { key: 'plural', image: `${S3_BASE_URL}/${imgs[1]}.png` }
          }
        };
      });
  }, [ageGroup, wordsData]);

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  const shuffledCards = useMemo(() => {
    if (!currentQuestion) return [];
    const cardsArray = [currentQuestion.options.singular, currentQuestion.options.plural];
    return shuffleArray(cardsArray);
    // currentQuestion starts undefined until useWordsData resolves — without
    // it as a dependency, this memo stays frozen at [] forever once that
    // happens after the initial (currentQuestionIndex-unchanged) render.
  }, [currentQuestionIndex, currentQuestion]);

  const speakWord = (audioUrl) => {
    if (hasSpokenRef.current) return;
    hasSpokenRef.current = true;
    setHasSpoken(true);
    setIsSpeaking(true);
    setIsDisabled(true);

    playInstruction('Послушай и выбери', () => {
      playWordAudio(audioUrl, () => {
        setIsSpeaking(false);
        setIsDisabled(false);
      });
    });
  };

  // ===== НОВАЯ ФУНКЦИЯ: ПОВТОР ИНСТРУКЦИИ =====
  const repeatInstruction = () => {
    if (!currentQuestion) return;
    // Озвучиваем напрямую, не трогая hasSpokenRef, чтобы не сломать логику авто-перехода
    stopWordAudio();
    stopInstruction();
    playInstruction('Послушай и выбери', () => {
      playWordAudio(currentQuestion.correctPhraseAudioUrl);
    });
  };

  useEffect(() => {
    if (currentQuestion && !showResult && !hasSpoken) {
      const timer = setTimeout(() => speakWord(currentQuestion.correctPhraseAudioUrl), 600);
      return () => clearTimeout(timer);
    }
  }, [currentQuestion, currentQuestionIndex, showResult, hasSpoken]);

  const handleCardClick = (card) => {
    if (isDisabled || isSpeaking) return;
    setIsDisabled(true);
    setSelectedCard(card.key);
    const isAnswerCorrect = card.key === 'singular'; 
    setIsCorrect(isAnswerCorrect);
    setAnswers((prev) => [...prev, isAnswerCorrect]);
    setAnswerLog((prev) => [...prev, {
      id: currentQuestion.id,
      label: INVERSION_LABELS[currentQuestion.id] || currentQuestion.id,
      correct: isAnswerCorrect,
    }]);

    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        goToNextQuestion();
      } else {
        setShowResult(true);
      }
    }, 1300);
  };

  const goToNextQuestion = () => {
    setCurrentQuestionIndex((prev) => prev + 1);
    setSelectedCard(null);
    setIsCorrect(null);
    setIsDisabled(false);
    setHasSpoken(false);
    hasSpokenRef.current = false;
  };

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
        const failedItems = answerLog.filter(a => !a.correct).map(a => a.label);

        if (onComplete) {
          onComplete({
            testId: 'SentenceComprehension',
            score,
            details: { total: totalQuestions, correct: correctCount, failedItems },
          });
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (showResult) {
    const correctCount = answers.filter((a) => a === true).length;
    return (
      <ModuleTemplate progress={answers} totalQuestions={totalQuestions} forceStop={true} onComplete={onComplete || onBack}>
        <div className="sentence-comp-result-content">
          <h2 className="sentence-comp-result-title">🎉 Упражнение завершено!</h2>
          <div className="sentence-comp-result-score">
            <span className="sentence-comp-score-number">{correctCount}</span>
            <span className="sentence-comp-score-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentQuestion) {
    return (
      <ModuleTemplate progress={answers} totalQuestions={totalQuestions} forceStop={true} onComplete={onComplete || onBack}>
        <div className="sentence-comp-loading-text">Загрузка упражнений...</div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalQuestions} 
      forceStop={true} 
      onComplete={onComplete}      
      onRepeat={repeatInstruction}    
      extraContent="Послушай и выбери"
    >
      <div className="sentence-comp-container">
        <div className={`sentence-comp-speaking-badge ${isSpeaking ? 'active' : ''}`}>
          <span className="sentence-comp-speaking-icon">🔊</span>
          <span className="sentence-comp-speaking-text">Слушай...</span>
        </div>

        <div className="sentence-comp-cards-grid">
          {shuffledCards.map((card) => {
            const isSelected = selectedCard === card.key;
            const showCorrect = isSelected && isCorrect === true;
            const showWrong = isSelected && isCorrect === false;
            const showCorrectHint = selectedCard === 'plural' && isCorrect === false && card.key === 'singular';

            return (
              <div
                key={card.key}
                className={`sentence-comp-card-wrapper ${!isDisabled && !isSpeaking ? 'clickable' : ''} ${showCorrect ? 'answer-correct' : ''} ${showWrong ? 'answer-wrong' : ''} ${showCorrectHint ? 'answer-correct-hint' : ''}`}
                onClick={() => handleCardClick(card)}
              >
                <img src={card.image} alt="" className="sentence-comp-card-image" onError={(e) => { e.target.src = '/placeholder.png'; }} />
              </div>
            );
          })}
        </div>
      </div>
    </ModuleTemplate>
  );
};

export default SentenceComprehension;